// @ts-check
/* eslint-disable no-console */
import axios from 'axios';
import Config from '../config';
import { OBJECT_STORES, IndexedDbService } from './indexedb.service';
import { monthsDiff, daysDiff, hoursDiff } from '../utils';

/**
 * @typedef imageObject
 * @property {number} id
 * @property {string} image
 * @property {Date} created_on
 */

const apiUrl = APP_SETTINGS.path.apiUrl;

/**
 * Enum for image keys
 * @readonly
 * @enum {number}
 */
export const IMAGE_KEYS = {
  PRINT_TICKET: 1,
  CATEGORY_PRINT: 2
};

const IMAGE_URLS = {
  [IMAGE_KEYS.PRINT_TICKET]: apiUrl + Config.endpoints.printTicketLogo,
  [IMAGE_KEYS.CATEGORY_PRINT]: apiUrl + Config.endpoints.printFixtureLogo,
};

export class ImageStorageService {
  constructor() {
    this.cacheTime = APP_SETTINGS.settings.imageCacheTime || null;
    this.store = new IndexedDbService(OBJECT_STORES.IMAGES);
    this.storeOpened = false;
  }
  /**
   * @public
   * call right after constructor to open db connection
   */
  async init() {
    await this.store.openDb().catch(e => {
      console.error(e); // eslint-disable-line
    });
  }
  /**
   * @private
   * Check if db is opened if not attempt to open it return false if it fails  
   * should be called at start of each public method
   */
  async checkConnection() {
    if (this.storeOpened) {
      return this.storeOpened;
    }
    let opened = true;
    await this.store.openDb().catch(e => {
      console.error(e); // eslint-disable-line
      opened = false;
    });
    this.storeOpened = opened;
    return opened;
  }
  /**
   * Runtime key validation
   * @param {IMAGE_KEYS} key 
   */
  validate(key) {
    return Object.values(IMAGE_KEYS).some(ik => ik === key);
  }
  /**
   * @private
   * Get image from db
   * @param {IMAGE_KEYS} key 
   */
  async getFromDb(key) {
    return await this.store.getItem(key).catch(e => {
      console.warn(e);
    });
  }
  /**
   * @private
   * Get image from url based on key and store it in db
   * @param {IMAGE_KEYS} key 
   */
  setImage(key) {
    return new Promise((res, rej) => {
      axios.get(IMAGE_URLS[key], { responseType: 'blob' }).then((resp) => {
        let reader = new FileReader();
        reader.readAsDataURL(resp.data);
        reader.onload = () => {
          const image = reader.result;
          const item = { id: key, image, created_on: new Date() };
          this.store.addItem(item).then(() => {
            res(item);
          }).catch(e => rej(e));
        };
      }).catch(e => rej(e));
    });
  }
  /**
   * @public
   * Get image from db or from api if it doesn't exist, can return undefined
   * @param {IMAGE_KEYS} key 
   * @returns {Promise<{id:number,image:string,created_on: Date}|undefined>}
   */
  async getImage(key) {
    const storeOpened = await this.checkConnection();
    if (!storeOpened) {
      return;
    }
    const valid = this.validate(key);
    if (!valid) {
      console.warn('Wrong IMAGE_KEY use IMAGE_KEYS enum to set the key');
      return;
    }
    const image = await this.getFromDb(key);
    if (image) {
      return image;
    }
    const newImage = await this.setImage(key).catch(e => {
      console.warn(e);
    });
    return newImage;
  }
  /**
   * @public
   * Check if images in db need to be removed (cache time has expired)  
   * called once from main app when app starts
   */
  async checkImageCache() {
    const storeOpened = await this.checkConnection();
    if (!storeOpened) {
      return;
    }
    let allImages = await this.store.getAllItems().catch(e => {
      console.warn(e);
    });
    if (allImages) {
      const now = new Date();
      allImages.forEach(item => {
        // if cache is null or undefined clear images from db
        if (!this.cacheTime) {
          this.store.deleteItem(item.id).catch(e => {
            console.warn(e);
          });
          return;
        }
        const checkMonths = this.cacheTime.match(/\d+m/);
        const checkDays = this.cacheTime.match(/\d+d/);
        const checkHours = this.cacheTime.match(/\d+h/);
        let mv = 0;
        if (checkMonths) {
          // get months differnce between image saved and now
          let mDiff = monthsDiff(item.created_on, now);
          mv = parseFloat(checkMonths[0]);
          if (mv > mDiff) {
            return;
          }
        }
        let dv = 0;
        if (checkDays) {
          // get days differnce between saved and now minus the months from above if any
          let dDiff = daysDiff(item.created_on, now) - (mv * 30);
          dv = parseFloat(checkDays[0]);
          if (dv > dDiff) {
            return;
          }
        }
        if (checkHours) {
          // get hours diff between saved and now minus the hours from days and months from above
          let hDiff = hoursDiff(item.created_on, now) - (mv * 30 * 24) - (dv * 24);
          let hv = parseFloat(checkHours[0]);
          if (hv > hDiff) {
            return;
          }
        }

        // delete item as cache has expired
        this.store.deleteItem(item.id).catch(e => {
          console.warn(e);
        });
      });
    }
  }
}